/**
 * AccountsAnnounceModal — a one-time, friendly heads-up for our
 * EXISTING customers, who have never had accounts before. The first
 * time they land on the homepage after we launch accounts, this
 * explains what's new (you now have an account) and that it's
 * passwordless (we email a one-time code), then points them to sign in.
 *
 * Self-contained (no redux): gated on the ACCOUNTS_ENABLED launch flag,
 * shown until the user acknowledges it (dismiss or CTA), tracked via
 * localStorage (ACCOUNTS_ANNOUNCE_KEY), and suppressed for anyone
 * already signed in. Best-effort — every storage touch is guarded.
 */
import React, { useEffect, useState } from "react";
import { Modal, ModalBody, Button } from "reactstrap";
import { useHistory, useLocation } from "react-router-dom";

import { ACCOUNTS_ENABLED, ACCOUNTS_ANNOUNCE_KEY } from "../../constants";
import { getToken } from "../../utils/tokenStorage";

// Mounted app-wide (App.js) so existing customers arriving via emailed
// links or SEO landing pages see the announcement too — not just
// homepage visitors. Suppressed on routes where an interruption could
// cost a conversion or block a task in progress.
const SUPPRESSED_PREFIXES = [
  "/login",
  "/dashboard",
  "/verify",
  "/submit",
  "/pre-order",
  "/preorder-complete",
  "/select-report",
  "/property-dossier",
  "/success",
  "/cancel",
  "/card-updated",
  "/subscribe",
  "/refund",
  "/coverage",
];

function alreadyShown() {
  try {
    return (
      typeof window !== "undefined" &&
      window.localStorage &&
      window.localStorage.getItem(ACCOUNTS_ANNOUNCE_KEY) === "1"
    );
  } catch (_e) {
    return false;
  }
}

function markShown() {
  try {
    window.localStorage.setItem(ACCOUNTS_ANNOUNCE_KEY, "1");
  } catch (_e) {
    /* storage unavailable — modal simply re-shows next visit */
  }
}

const ROW = {
  display: "flex",
  alignItems: "flex-start",
  gap: "0.7rem",
  textAlign: "left",
  marginBottom: "0.7rem",
  fontSize: "0.98rem",
  color: "#334155",
  lineHeight: 1.45,
};
const ICON = { fontSize: "1.25rem", lineHeight: 1.2, flexShrink: 0 };

const AccountsAnnounceModal = () => {
  const history = useHistory();
  const { pathname } = useLocation();
  const suppressed = SUPPRESSED_PREFIXES.some((p) => pathname.startsWith(p));
  const [open, setOpen] = useState(false);

  useEffect(() => {
    // Don't announce a feature that isn't live; don't pester signed-in
    // users, anyone who's already seen it, or anyone mid-funnel.
    if (!ACCOUNTS_ENABLED) return undefined;
    if (suppressed) return undefined;
    if (getToken()) return undefined;
    if (alreadyShown()) return undefined;

    const t = setTimeout(() => setOpen(true), 1200);
    return () => clearTimeout(t);
  }, [suppressed]);

  // Persist only on explicit interaction — someone who navigates away
  // without dismissing gets the announcement again next visit.
  const close = () => {
    markShown();
    setOpen(false);
  };
  const goSignIn = () => {
    markShown();
    setOpen(false);
    history.push("/login");
  };

  if (!open) return null;

  return (
    <Modal
      isOpen={open}
      toggle={close}
      centered
      backdrop
      data-testid="accounts-announce-modal"
    >
      <ModalBody style={{ textAlign: "center", padding: "2.2rem 2rem 1.9rem", background: "#fff" }}>
        <div aria-hidden="true" style={{ fontSize: "3rem", marginBottom: "0.4rem", lineHeight: 1 }}>
          🎉
        </div>
        <h3
          style={{
            color: "#102a43",
            fontWeight: 800,
            margin: "0 0 0.4rem",
            letterSpacing: "-0.02em",
            fontSize: "1.7rem",
          }}
        >
          You now have an account
        </h3>
        <p style={{ fontSize: "1.05rem", color: "#334155", margin: "0 0 1.3rem", lineHeight: 1.5 }}>
          We've added accounts so everything we do for you lives in one place —
          no extra setup needed.
        </p>

        <div style={{ maxWidth: 380, margin: "0 auto 1.4rem" }}>
          <div style={ROW}>
            <span style={ICON} aria-hidden="true">📄</span>
            <span>
              <strong>All your reports &amp; savings</strong> — every property,
              every year, in one dashboard.
            </span>
          </div>
          <div style={ROW}>
            <span style={ICON} aria-hidden="true">🔑</span>
            <span>
              <strong>No password to remember</strong> — we email you a one-time
              code each time you sign in (you can set a password if you prefer).
            </span>
          </div>
          <div style={ROW}>
            <span style={ICON} aria-hidden="true">⚙️</span>
            <span>
              <strong>Manage everything</strong> — view filing instructions and
              cancel or pause your subscription anytime.
            </span>
          </div>
        </div>

        <Button
          color="primary"
          size="lg"
          onClick={goSignIn}
          style={{
            minWidth: "240px",
            fontWeight: 600,
            background: "#F5A88A",
            borderColor: "#F5A88A",
            color: "#2E3A52",
            borderRadius: "999px",
          }}
          data-testid="accounts-announce-cta"
        >
          Sign in with your email
        </Button>
        <div style={{ marginTop: "0.9rem" }}>
          <button
            type="button"
            onClick={close}
            style={{
              background: "none",
              border: "none",
              color: "#64748b",
              fontSize: "0.92rem",
              cursor: "pointer",
              fontWeight: 500,
            }}
          >
            Maybe later
          </button>
        </div>
        <p style={{ fontSize: "0.82rem", color: "#8A93A3", margin: "1rem 0 0" }}>
          Use the email you ordered with — we'll find your reports automatically.
        </p>
      </ModalBody>
    </Modal>
  );
};

export default AccountsAnnounceModal;
